
/*
 * Copyright (c) 2023. Data Smart Technologies CC
 * https://www.datasmart.co.za
 * View license agreement and terms of use at https://www.datasmart.co.za/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2023-06-29
 */

import React, { ChangeEvent, useEffect, useState } from "react";
import { Button, Card, Container, Modal } from "react-bootstrap";
import { useAuth } from "../../../../contexts/AuthContext";
import { QueryListProvider } from "../../../../component/solution/QueryList/QueryListContext";
import { QueryList } from "../../../../component/solution/QueryList/QueryList";
import { Search, X } from "lucide-react";
import { formatDateFull } from "../../../../component/solution/utils/AppUtil";
import { CastType, IQuery, IWhere } from "../../../../component/solution/Interfaces";

enum MovementOutStatus {
    TO_SEND = "TO_SEND",
    SENT = "SENT",
    SUCCESS = "SUCCESS", // M24L Validation Success
    ERROR = "ERROR", // M24L Validation Failed
    PICKING = "PICKING",
    CLOSED = "CLOSED"
}

export const StockMovementOut: React.FC = () => {
    
        const detailQueryState: IQuery = {
            tableName: "sa_m24l_stock_movement_out_line",
            select: [
            ],
            groupBy: [],
            join: [],
            orderBy: [
                {
                    fullColumnName: "item_code",
                    direction: "ASC",
                    cast: CastType.STRING
                }
            ],
            where: [
                {
                    fullColumnName: "doc_num",
                    value: "",
                    operator: "EQ",
                    cast: CastType.STRING
                }
            ],
            mainTableOnly: false,
            includeExtraColumns: false,
            includeControlColumns: true,
            modifiedAfter: null,
            touchedBefore: null,
            isDirty: null,
            pageNum: 0,
            pageSize: 5000,
            frontEndOnly: null
        }


    const generateDetailTables = (detail: any[]) => {
        const detailTable: React.ReactElement[] = [];
        let totalQuantityOrdered = 0;
        let totalQuantityReceived = 0;
        let totalQuantitySent = 0;

        // Sort by line_num ascending before rendering
        const sortedDetail = [...detail].sort((a, b) => {
            const aLine = a.data?.line_num ?? 0;
            const bLine = b.data?.line_num ?? 0;
            return aLine - bLine;
        });

        sortedDetail.forEach((item, idx) => {
            const data = item.data;
            totalQuantityOrdered += data?.qty_ordered ?? 0;
            totalQuantityReceived += data?.qty_received ?? 0;
            totalQuantitySent += data?.qty_sent ?? 0;

            detailTable.push(
            <tr key={idx}>
                <td className="text-center" style={{ border: '1px solid #adb5bd' }}>{data?.line_num}</td>
                <td className="text-center" style={{ border: '1px solid #adb5bd' }}>{data?.item_code}</td>
                <td className="text-center" style={{ border: '1px solid #adb5bd' }}>{data?.from_warehouse}</td>
                <td className="text-center" style={{ border: '1px solid #adb5bd' }}>{data?.to_warehouse}</td>
                <td className="text-center" style={{ border: '1px solid #adb5bd' }}>{data?.qty_ordered}</td>
                <td className="text-center" style={{ border: '1px solid #adb5bd' }}>{data?.qty_sent}</td>
                <td className="text-center" style={{ border: '1px solid #adb5bd' }}>{data?.qty_received}</td>
            </tr>
            );
        });

        const summaryTable = (
           <table className="table w-auto" style={{ border: '1px solid #adb5bd' }}>
            <tbody>
                <tr>
                    <th style={{ width: '150px', border: '1px solid #adb5bd' }}>Unique Item Total</th>
                    <td style={{ width: '100px',  border: '1px solid #adb5bd' }} className="text-end">{detail.length}</td>
                </tr>
                 <tr>
                    <th style={{ width: '150px', border: '1px solid #adb5bd' }}>Total Qty Ordered</th>
                    <td style={{ width: '100px',  border: '1px solid #adb5bd' }} className="text-end">{totalQuantityOrdered}</td>
                </tr>
                 <tr>
                    <th style={{ width: '150px', border: '1px solid #adb5bd' }}>Total Qty Sent</th>
                    <td style={{ width: '100px',  border: '1px solid #adb5bd' }} className="text-end">{totalQuantitySent}</td>
                </tr>
                 <tr>
                    <th style={{ width: '150px', border: '1px solid #adb5bd' }}>Total Qty Received</th>
                    <td style={{ width: '100px',  border: '1px solid #adb5bd' }} className="text-end">{totalQuantityReceived}</td>
                </tr>
            </tbody>
        </table>
        );

        return { summaryTable, detailTable };
    };

    /*
    Status 

    Outbound - Retail
    TO_SEND - Document ready to be sent to M24L.
    SENT - Doument has has been sent to M24L for processing.
    SUCCESS - document successfully received at M24L for processing.
    ERROR - There was a problem integrating the document at M24L.
    PICKING - M24L have started picking the stock.
    DISPATCHED - Stock dispatched from M24L.
    TO_CLOSE - Stock has been received at the store and pending update to SAP.
    CLOSED - Document processing complete.

    */


    return(
        <React.Fragment>
            <Container fluid className="p-0">
            <QueryListProvider>
                    <QueryList
                        listTitle={"Outbound - Retail"}
                        recordTitle={"Outbound - Retail"}
                        tableName={"sa_m24l_stock_movement_out"}
                        editTableName={"sa_m24l_stock_movement_out"}
                        hasIntegrationStatusLegend={true} 
                        errorCategoryName={'MovementOut'}
                        integrationstatusLegend={[
                            { status: 'ERROR', color: '#bf3636', description: 'There was a problem integrating the document at M24L.'},
                            { status: 'TO_SEND', color: '#d4a53f', description: 'Document ready to be sent to M24L.'},
                            { status: 'PICKING', color: '#d4a53f', description: 'M24L have started picking the stock.'},
                            { status: 'DISPATCHED', color: '#d4a53f', description: 'Stock dispatched from M24L.'},
                            { status: 'SENT', color: '#d4a53f', description: 'Doument has been sent to M24L for processing.'},
                            { status: 'SUCCESS', color: '#d4a53f', description: 'Document successfully received at M24L for processing.'},
                            { status: 'TO_CLOSE', color: '#d4a53f', description: 'Stock has been received at the store and pending update to SAP.'},
                            { status: 'CLOSED', color: '#3ec76b', description: 'Document processing complete.'}
                        ]}
                        modal={
                            {
                              modalTitle: "Doc Num:",
                              modalSubtitle: "Outbound - Retail:",  //Shipment Confirmation
                              modalProptBy: "doc_num",
                              tableName: "sa_m24l_stock_movement_out",
                              tableLineQuery: "sa_m24l_stock_movement_out_line",
                              modalAction: 'viewing',
                              modalHasViewing: true,
                              queryData: detailQueryState,
                              processData: (result: any) => {
                                 
                                const detail: any[] = result.data;
                                const { summaryTable, detailTable } = generateDetailTables(detail);

                                 return (
                                      <div className="d-flex flex-column gap-3" >
                                        <div style={{background:"#f2f5f3"}} className="p-2">
                                            <h6 className="text-secondary fw-bolder">
                                               Summary
                                            </h6>
                                            <div className="mb-3">{summaryTable}</div>
                                        </div>

                                        <div style={{background:"#f2f5f3"}} className="p-2">
                                            <h6 className="text-secondary fw-bolder">
                                                Line Details
                                            </h6>
                                            <table className="table" style={{ border: '1px solid #adb5bd' }}>
                                                <thead>
                                                    <tr>
                                                        <th style={{ border: '1px solid #adb5bd' }}>Line No.</th>
                                                        <th style={{ border: '1px solid #adb5bd' }}>Item Code</th>
                                                        <th style={{ border: '1px solid #adb5bd' }}>From Warehouse</th>
                                                        <th style={{ border: '1px solid #adb5bd' }}>To Warehouse</th>
                                                        <th style={{ border: '1px solid #adb5bd' }}>Qty Ordered</th>
                                                        <th style={{ border: '1px solid #adb5bd' }}>Qty Sent</th>
                                                        <th style={{ border: '1px solid #adb5bd' }}>Qty Received</th>
                                                    </tr>
                                                </thead>
                                                <tbody>{detailTable}</tbody>
                                            </table>
                                        </div>
                                    </div>
                                 );
                              }
                            }
                        }
                        editHasCompoundKey={false}
                        join={[{
                            relationship: "notifications",
                            joinType: "LEFT"
                        }]} 
                        select={[
                            {
                                fullColumnName: "doc_num",
                                aggregate: ""
                            },
                            {
                                fullColumnName: "division",
                                aggregate: ""
                            },
                            {
                                fullColumnName: "card_code",
                                aggregate: ""
                            },
                            {
                                fullColumnName: "card_name",
                                aggregate: ""
                            },
                            {
                                fullColumnName: "num_at_card",
                                aggregate: ""
                            },
                            {
                                fullColumnName: "from_warehouse",
                                aggregate: ""
                            }, 
                            {
                                fullColumnName: "to_warehouse",
                                aggregate: ""
                            },  
                            {
                                fullColumnName: "integration_status",
                                aggregate: ""
                            },        
                            {
                                fullColumnName: "total_qty",
                                aggregate: ""
                            },     
                            {
                                fullColumnName: "comments",
                                aggregate: ""
                            },                                  
                            {
                                fullColumnName: "created",
                                aggregate: ""
                            },
                            {
                                fullColumnName: "updated",
                                aggregate: ""
                            },
                            {
                                fullColumnName: "notifications.reference",
                                aggregate: "COUNT"
                            },
                            
                        ]}
                        where={[]} 
                        orderBy={[    
                            {
                              fullColumnName: "created",
                              direction: "DESC",
                              cast: CastType.TIMESTAMP
                            },
                         ]} 
                        groupBy={[
                            "doc_num",
                            "division", 
                            "card_code",
                            "card_name",
                            "num_at_card",
                            "from_warehouse",
                            "to_warehouse",
                            "integration_status",
                            "total_qty",
                            "comments",
                            "created",
                            "updated",
                        ]}    
                        
                    />
                </QueryListProvider>
            </Container>
        </React.Fragment>
    );
}